import {ILabelsCalendar, ILabelsRestrictionCard} from '@ryndem/pqf-design-system-next-gen';
import {
  ILabelsConfigModal,
  ILabelsModalDelete,
  ILabelsModalEdit,
  RestriccionFechasPreviewExtended,
} from '@appModels/delivery-restrictions/delivery-restrictions.models';

/**
 * @description Interface representing all i18n translation labels
 * required by the Delivery Restrictions module.
 * @property {string[]} months - List of month name translations.
 * @property {string[]} days - List of full day name translations.
 * @property {string[]} daysAbbreviated - List of abbreviated day name translations.
 * @property {ILabelsRestrictionCard} labelsCards - Labels for the restriction card components.
 * @property {ILabelsCalendar} labelsCalendar - Labels for the calendar component.
 * @property {ILabelsConfigModal} labelsConfigModal - Labels for the restriction configuration modal.
 * @property {ILabelsModalDelete} labelsModalDelete - Labels for the delete confirmation modal.
 * @property {ILabelsModalEdit} labelsModalEdit - Labels for the edit confirmation modal.
 */
export interface ITranslateDeliveryRestrictions {
  months: string[];
  days: string[];
  daysAbbreviated: string[];
  labelsCards: ILabelsRestrictionCard;
  labelsCalendar: ILabelsCalendar;
  labelsConfigModal: ILabelsConfigModal;
  labelsModalDelete: ILabelsModalDelete;
  labelsModalEdit: ILabelsModalEdit;
}

/**
 * @description Interface representing the NgRx state slice for the Delivery Restrictions feature.
 * @property {RestriccionFechasPreviewExtended[]} deliveryRestrictionSavedConfiguration - Restrictions already persisted in the backend.
 * @property {RestriccionFechasPreviewExtended[]} deliveryRestrictionConfiguration - New restrictions pending preview and save.
 * @property {RestriccionFechasPreviewExtended[]} deliveryRestrictionsToEdit - Restrictions that have been modified and are pending update.
 * @property {string[]} deliveryRestrictionsToDelete - IDs of restrictions pending deletion.
 * @property {string} typeContext - type context.
 * @property {RestriccionFechasPreviewExtended[]} backUpDeliveryRestrictions - Backup of the original restrictions before any edits, used for rollback.
 * @property {boolean} editMode - Whether the module is currently in edit mode.
 * @property {ITranslateDeliveryRestrictions} translate - All i18n translation labels for the module.
 */
export interface DeliveryRestrictionsState {
  deliveryRestrictionSavedConfiguration: RestriccionFechasPreviewExtended[];
  deliveryRestrictionConfiguration: RestriccionFechasPreviewExtended[];
  deliveryRestrictionsToEdit: RestriccionFechasPreviewExtended[];
  deliveryRestrictionsToDelete: string[];
  typeContext: string;
  backUpDeliveryRestrictions: RestriccionFechasPreviewExtended[];
  isEditMode: boolean;
  translate: ITranslateDeliveryRestrictions;
}

/**
 * @description Factory function that returns the initial state for the Delivery Restrictions feature.
 * All restriction arrays are initialized as empty, edit mode is disabled,
 * and translations are set as an empty object pending initialization.
 * @returns {DeliveryRestrictionsState} The default initial state.
 */
export const initialDeliveryRestrictionsState = (): DeliveryRestrictionsState => ({
  deliveryRestrictionSavedConfiguration: [],
  deliveryRestrictionConfiguration: [],
  deliveryRestrictionsToEdit: [],
  deliveryRestrictionsToDelete: [],
  backUpDeliveryRestrictions: [],
  typeContext: null,
  isEditMode: false,
  translate: {} as ITranslateDeliveryRestrictions,
});
